import { ExclaimCircleIcon, Flex, IconButton, Text, Tooltip, palette } from "@minervaproject/fds";
import { ReactElement, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { LINKS } from "./local_modal/preclass_check/preclass_check_messages";
import { OverlayError } from "./overlay_error";

import { useAppDispatch } from "hooks/redux_hooks";
import { useSidebarAdjustment } from "hooks/use_sidebar_adjustment";
import { setOverlayError } from "store/reducers/local";

class _FlightErrorBoundary extends ErrorBoundary {
  componentDidCatch(error: Error): void {
    const hasLogger = window.flightLogger && typeof window.flightLogger.error === "function";

    try {
      if (hasLogger) {
        window.flightLogger.error("[FlightErrorBoundary]", error);
      } else {
        console.error("[FlightErrorBoundary] Logger not initialized", error);
      }
    } catch (loggingError) {
      console.error("[FlightErrorBoundary] Failed to log error", loggingError, error);
    }
  }
}

const DefaultFallback = ({
  text,
  includeTechSupport = false,
  borderRadius = "4px",
  margin,
  width = undefined,
  height = undefined,
}: {
  text: string;
  includeTechSupport?: boolean;
  borderRadius?: string;
  margin: string;
  width?: string;
  height?: string;
}) => {
  return (
    <div
      style={{
        display: "grid",
        alignItems: "start",
        columnGap: "8px",
        gridTemplateColumns: "max-content 1fr max-content",
        backgroundColor: "var(--orange-tint-90)",
        borderRadius,
        padding: "8px 12px",
        margin,
        width,
        height,
      }}
    >
      <ExclaimCircleIcon color="orange" mt="2px" size="lg" />
      <div className="body-s text-black">
        <Text>
          {includeTechSupport ? (
            <p>
              {text} Please contact{" "}
              <a target="_blank" rel="noopener noreferrer" href={LINKS.liveTechSupport}>
                Live Tech Support
              </a>{" "}
              for immediate assistance.
            </p>
          ) : (
            <p>{text}</p>
          )}
        </Text>
      </div>
    </div>
  );
};

function FlightErrorBoundary({
  children,
  fallback,
  text = "Something went wrong.",
  includeTechSupport = false,
  margin = "0 8px",
  width = undefined,
  height = undefined,
}: {
  children: React.ReactNode;
  fallback?: ReactElement;
  text?: string;
  includeTechSupport?: boolean;
  margin?: string;
  width?: string;
  height?: string;
}) {
  const Fallback = fallback || (
    <DefaultFallback
      text={text}
      includeTechSupport={includeTechSupport}
      margin={margin}
      width={width}
      height={height}
    />
  );
  return (
    // eslint-disable-next-line react/jsx-pascal-case
    <_FlightErrorBoundary fallback={Fallback}>{children}</_FlightErrorBoundary>
  );
}

function HeaderErrorBoundary({ children, text }: { children: React.ReactNode; text: string }) {
  const widthAdjustment = useSidebarAdjustment(16);
  return (
    <FlightErrorBoundary
      fallback={<DefaultFallback text={text} margin="0" borderRadius="0" width={widthAdjustment} />}
    >
      {children}
    </FlightErrorBoundary>
  );
}

function CaptionsOutputErrorBoundary({
  children,
  text,
  userHasLiveCaptionsEnabled,
}: {
  children: React.ReactNode;
  text: string;
  userHasLiveCaptionsEnabled: boolean;
}) {
  return (
    <FlightErrorBoundary
      fallback={
        userHasLiveCaptionsEnabled ? (
          <div
            style={{
              backgroundColor: "var(--orange-tint-90)",
              borderRadius: "4px",
              padding: "8px 12px",
              margin: "4px",
            }}
          >
            <div style={{ display: "flex", margin: "0 auto", width: "fit-content" }}>
              <ExclaimCircleIcon color="orange" mt="2px" mr="8px" size="lg" />
              <div className="body-s text-black" style={{ width: "fit-content" }}>
                {text}
              </div>
            </div>
          </div>
        ) : (
          <NullFallback />
        )
      }
    >
      {children}
    </FlightErrorBoundary>
  );
}

function CenteredFallback({
  text,
  includeTechSupport,
}: {
  text: string;
  includeTechSupport?: boolean;
}) {
  return (
    <div style={{ display: "grid", placeItems: "center", height: "100%", margin: "0 auto" }}>
      <DefaultFallback
        text={text}
        includeTechSupport={includeTechSupport}
        margin="0"
        width="fit-content"
        height="fit-content"
      />
    </div>
  );
}
function CenteredErrorBoundary({
  children,
  text,
  includeTechSupport,
}: {
  children: React.ReactNode;
  text: string;
  includeTechSupport?: boolean;
}) {
  return (
    <FlightErrorBoundary
      fallback={<CenteredFallback text={text} includeTechSupport={includeTechSupport} />}
    >
      {children}
    </FlightErrorBoundary>
  );
}

function SidebarErrorBoundary({
  children,
  text,
  title = "",
}: {
  children: React.ReactNode;
  text: string;
  title?: string;
}) {
  return (
    <FlightErrorBoundary
      fallback={
        <Flex className="sidebar-width">
          <Flex direction="column" width="100%">
            <Text className="body-l flex-none" kind="h2" p={2}>
              {title}
            </Text>
            <DefaultFallback text={text} margin="0 8px" />
          </Flex>
        </Flex>
      }
    >
      {children}
    </FlightErrorBoundary>
  );
}

function ModalErrorBoundary({
  children,
  text,
  hideModal,
}: {
  children: React.ReactNode;
  text: string;
  hideModal: () => void;
}) {
  return (
    <FlightErrorBoundary fallback={<OverlayError text={text} hideModal={hideModal} />}>
      {children}
    </FlightErrorBoundary>
  );
}

function OverlayErrorFallback({ text }: { text: string }) {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(setOverlayError({ error: text }));
  }, [dispatch, text]);
  return null;
}

const ERROR_MESSAGE = "Something is wrong. The classroom might not perform as expected.";
function OverlayErrorBoundary({
  children,
  text = ERROR_MESSAGE,
}: {
  children: React.ReactNode;
  text?: string;
}) {
  return (
    <FlightErrorBoundary fallback={<OverlayErrorFallback text={text} />}>
      {children}
    </FlightErrorBoundary>
  );
}

function TableRowErrorBoundary({
  children,
  colSpan,
  text,
  margin = "0 8px",
}: {
  children: React.ReactNode;
  colSpan: number;
  text: string;
  margin?: string;
}) {
  return (
    <FlightErrorBoundary
      fallback={
        <tr>
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          <td colSpan={colSpan}>
            <DefaultFallback text={text} margin={margin} />
          </td>
        </tr>
      }
      margin={margin}
      text={text}
    >
      {children}
    </FlightErrorBoundary>
  );
}

const NullFallback = () => null;

function NullErrorBoundary({ children }: { children: React.ReactNode }) {
  return <FlightErrorBoundary fallback={<NullFallback />}>{children}</FlightErrorBoundary>;
}

const WarningButton = () => (
  <IconButton>
    <ExclaimCircleIcon width={16} height={16} color="orange" />
  </IconButton>
);

function TooltipErrorBoundary({ children, text }: { children: React.ReactNode; text: string }) {
  return (
    <FlightErrorBoundary
      fallback={
        <Tooltip backgroundColor={palette.white} iconComponent={<WarningButton />}>
          <div
            style={{
              display: "grid",
              alignItems: "start",
              columnGap: "8px",
              gridTemplateColumns: "max-content 1fr max-content",
            }}
          >
            <ExclaimCircleIcon color="orange" mt="2px" size="lg" />
            <div className="body-s text-black">{text}</div>
          </div>{" "}
        </Tooltip>
      }
    >
      {children}
    </FlightErrorBoundary>
  );
}

export {
  CaptionsOutputErrorBoundary,
  CenteredErrorBoundary,
  CenteredFallback,
  FlightErrorBoundary,
  HeaderErrorBoundary,
  ModalErrorBoundary,
  NullErrorBoundary,
  OverlayErrorBoundary,
  SidebarErrorBoundary,
  TableRowErrorBoundary,
  TooltipErrorBoundary,
};
